import React from "react";
import {
  fetchGatewayStatusBatches,
  fetchGatewayStatusDaily,
  fetchGatewayStatusOverview,
} from "../api/gatewayStatus";

const nf = new Intl.NumberFormat("en-US");

const formatNumber = (value) => nf.format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString();
};

const formatPeriod = (start, end) => {
  if (!start && !end) return "-";
  if (!start || !end || start === end) return formatDateTime(start || end);
  const startDate = new Date(start).toLocaleDateString();
  const endDate = new Date(end).toLocaleDateString();
  if (startDate === endDate) {
    return `${startDate}, ${formatTime(start)} - ${formatTime(end)}`;
  }
  return `${formatDateTime(start)} - ${formatDateTime(end)}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(`${value}T00:00:00Z`).toLocaleDateString();
};

const formatRelative = (value) => {
  if (!value) return "-";
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${Math.floor(diffHour / 24)}d ago`;
};

const pct = (part, total) => {
  if (!total) return "0.0%";
  return `${((Number(part || 0) / Number(total || 0)) * 100).toFixed(1)}%`;
};

const modeLabel = (mode) => {
  if (mode === "gateway_api") return "API";
  if (mode === "off") return "OFF";
  return "MODBUS";
};

const healthClass = (state) => {
  if (state === "ok") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (state === "stale") return "bg-amber-50 text-amber-800 border-amber-200";
  if (state === "error") return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-slate-50 text-slate-600 border-slate-200";
};

function StatCard({ label, value, detail }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-950">{value}</div>
      {detail && <div className="mt-1 text-sm text-slate-500">{detail}</div>}
    </div>
  );
}

function BarCell({ accepted, overlap, duplicate, total }) {
  const acceptedPct = total ? (accepted / total) * 100 : 0;
  const overlapPct = total ? (overlap / total) * 100 : 0;
  const duplicatePct = total ? (duplicate / total) * 100 : 0;
  return (
    <div>
      <div className="flex h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="bg-emerald-500" style={{ width: `${Math.min(100, acceptedPct)}%` }} />
        <div className="bg-sky-400" style={{ width: `${Math.min(100, overlapPct)}%` }} />
        <div className="bg-amber-400" style={{ width: `${Math.min(100, duplicatePct)}%` }} />
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {pct(accepted, total)} inserted / {pct(overlap, total)} overlap
      </div>
    </div>
  );
}

export default function ScadaAnalytics() {
  const [overview, setOverview] = React.useState({ totals: {}, sites: [] });
  const [selectedSiteId, setSelectedSiteId] = React.useState("");
  const [batches, setBatches] = React.useState([]);
  const [batchTotal, setBatchTotal] = React.useState(0);
  const [batchPage, setBatchPage] = React.useState(0);
  const [batchPageSize, setBatchPageSize] = React.useState(20);
  const [daily, setDaily] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const loadOverview = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchGatewayStatusOverview();
      setOverview(data);
      setSelectedSiteId((current) => {
        if (current && data.sites?.some((site) => String(site.site_id) === String(current))) return current;
        const firstWithBatches = data.sites?.find((site) => site.total_batches > 0);
        return String(firstWithBatches?.site_id || data.sites?.[0]?.site_id || "");
      });
    } catch (err) {
      setError("Unable to load gateway status.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  React.useEffect(() => {
    if (!selectedSiteId) {
      setBatches([]);
      setBatchTotal(0);
      setDaily([]);
      return;
    }
    let cancelled = false;
    const loadDetails = async () => {
      setDetailLoading(true);
      try {
        const [batchData, dailyData] = await Promise.all([
          fetchGatewayStatusBatches(Number(selectedSiteId), batchPageSize, batchPage * batchPageSize),
          fetchGatewayStatusDaily(Number(selectedSiteId), 30),
        ]);
        if (cancelled) return;
        setBatches(batchData.batches || []);
        setBatchTotal(batchData.total || 0);
        setDaily(dailyData.days || []);
      } catch (err) {
        if (!cancelled) {
          setBatches([]);
          setBatchTotal(0);
          setDaily([]);
          setError("Unable to load selected site gateway details.");
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedSiteId, batchPage, batchPageSize]);

  React.useEffect(() => {
    setBatchPage(0);
  }, [selectedSiteId, batchPageSize]);

  const totals = overview.totals || {};
  const sites = overview.sites || [];
  const selectedSite = sites.find((site) => String(site.site_id) === String(selectedSiteId));
  const totalBatchPages = Math.max(1, Math.ceil(batchTotal / batchPageSize));
  const batchStart = batchTotal === 0 ? 0 : batchPage * batchPageSize + 1;
  const batchEnd = Math.min(batchTotal, (batchPage + 1) * batchPageSize);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Gateway Status</h1>
          <p className="mt-2 text-sm text-slate-600">
            Monitor gateway telemetry uploads, central Modbus overlaps, inserted samples and recent batch health.
          </p>
        </div>
        <button
          type="button"
          onClick={loadOverview}
          className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-500"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Batches" value={formatNumber(totals.total_batches)} detail={`${formatNumber(totals.sites_with_batches)} active sites`} />
        <StatCard label="Samples Received" value={formatNumber(totals.sample_count)} detail="Gateway payload rows" />
        <StatCard label="Inserted" value={formatNumber(totals.accepted_sample_count)} detail={pct(totals.accepted_sample_count, totals.sample_count)} />
        <StatCard label="Central Overlap" value={formatNumber(totals.central_modbus_overlap_count)} detail={pct(totals.central_modbus_overlap_count, totals.sample_count)} />
        <StatCard label="Rejected" value={formatNumber(totals.rejected_batches)} detail={`${formatNumber(totals.duplicate_sample_count)} duplicate samples`} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <h2 className="font-semibold text-slate-900">Sites</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading gateway status...</div>
        ) : sites.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No sites available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Site</th>
                  <th className="px-4 py-3">Mode</th>
                  <th className="px-4 py-3">Health</th>
                  <th className="px-4 py-3 text-right">Batches</th>
                  <th className="px-4 py-3 text-right">Samples</th>
                  <th className="px-4 py-3">Distribution</th>
                  <th className="px-4 py-3">Last Batch</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sites.map((site) => (
                  <tr
                    key={site.site_id}
                    className={`cursor-pointer hover:bg-slate-50 ${
                      String(site.site_id) === String(selectedSiteId) ? "bg-blue-50/60" : ""
                    }`}
                    onClick={() => setSelectedSiteId(String(site.site_id))}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{site.site_name}</div>
                      <div className="text-xs text-slate-500">Site {site.site_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {modeLabel(site.acquisition_mode)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${healthClass(site.health_state)}`}>
                        {site.health_state}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatNumber(site.total_batches)}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(site.sample_count)}</td>
                    <td className="min-w-56 px-4 py-3">
                      <BarCell
                        accepted={site.accepted_sample_count}
                        overlap={site.central_modbus_overlap_count}
                        duplicate={site.duplicate_sample_count}
                        total={site.sample_count}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{formatRelative(site.last_batch_received_at)}</div>
                      <div className="text-xs text-slate-500">{formatDateTime(site.last_cycle_timestamp)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.25fr,1fr]">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div>
              <h2 className="font-semibold text-slate-900">Recent Batches</h2>
              <p className="text-sm text-slate-500">{selectedSite?.site_name || "Select a site"}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-500">
                {formatNumber(batchStart)}-{formatNumber(batchEnd)} of {formatNumber(batchTotal)}
              </span>
              <select
                value={batchPageSize}
                onChange={(event) => setBatchPageSize(Number(event.target.value))}
                className="rounded-lg border border-slate-200 px-2 py-1 text-slate-700"
              >
                {[20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={batchPage === 0 || detailLoading}
                onClick={() => setBatchPage((page) => Math.max(0, page - 1))}
                className="rounded-lg border border-slate-200 px-3 py-1 font-semibold text-slate-600 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={batchPage + 1 >= totalBatchPages || detailLoading}
                onClick={() => setBatchPage((page) => page + 1)}
                className="rounded-lg border border-slate-200 px-3 py-1 font-semibold text-slate-600 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
          {detailLoading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading batches...</div>
          ) : batches.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No gateway batches for this site.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Batch</th>
                    <th className="px-4 py-3">Raw Period</th>
                    <th className="px-4 py-3 text-right">Inserted</th>
                    <th className="px-4 py-3 text-right">Overlap</th>
                    <th className="px-4 py-3">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {batches.map((batch) => (
                    <tr key={batch.id}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{batch.batch_id}</div>
                        <div className="text-xs text-slate-500">{batch.status}</div>
                      </td>
                      <td className="px-4 py-3">{formatPeriod(batch.sample_period_start, batch.sample_period_end)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                        {formatNumber(batch.accepted_sample_count)}
                      </td>
                      <td className="px-4 py-3 text-right text-sky-700">
                        {formatNumber(batch.central_modbus_overlap_count)}
                      </td>
                      <td className="px-4 py-3">
                        <div>{formatRelative(batch.received_at)}</div>
                        <div className="text-xs text-slate-500">{formatNumber(batch.sample_count)} samples</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="font-semibold text-slate-900">Daily Rollup</h2>
            <p className="text-sm text-slate-500">Last 30 days by gateway cycle timestamp</p>
          </div>
          {detailLoading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading daily rollup...</div>
          ) : daily.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No daily gateway rollup yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {daily.map((day) => (
                <div key={day.date} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">{formatDate(day.date)}</div>
                      <div className="text-xs text-slate-500">{formatNumber(day.batch_count)} batches</div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="font-semibold text-emerald-700">{formatNumber(day.accepted_sample_count)} inserted</div>
                      <div className="text-sky-700">{formatNumber(day.central_modbus_overlap_count)} overlap</div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <BarCell
                      accepted={day.accepted_sample_count}
                      overlap={day.central_modbus_overlap_count}
                      duplicate={day.duplicate_sample_count}
                      total={day.sample_count}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
